/**
 * useHotkeys — the ONE React hook to bind keyboard shortcuts for a component's
 * lifetime (CLAUDE.md standing rule 14, §18.6, ADR-0013).  NEVER write a
 * `document.addEventListener('keydown', …)` in a feature file — use this.
 *
 * Handlers are captured fresh through a ref, so a binding always runs the latest
 * closure (no stale state) without re-registering on every render.  Bindings are
 * re-registered only when their serializable shape (combo/label/group/disabled/…)
 * changes, which keeps the cheat-sheet snapshot (labels, greyed state) accurate.
 * Everything is unregistered automatically on unmount.
 */
import { useEffect, useRef } from 'react';
import { registerHotkey, type HotkeyDef } from './hotkeyStore';

export function useHotkeys(input: HotkeyDef | HotkeyDef[] | null | undefined): void {
  const defs = input == null ? [] : Array.isArray(input) ? input : [input];

  // Latest defs every render → handlers never go stale.
  const ref = useRef<HotkeyDef[]>(defs);
  ref.current = defs;

  // A signature of the serializable fields — the effect re-runs (re-registers)
  // only when one of these changes, not when a handler identity changes.
  const sig = defs
    .map((d) => [
      d.combo, d.group, d.label, d.disabled ? 1 : 0, d.order ?? '',
      d.hidden ? 1 : 0, d.allowInInput ?? '', d.preventDefault ?? '',
    ].join('|'))
    .join('~');

  useEffect(() => {
    const unregs = ref.current.map((d, i) =>
      registerHotkey({
        combo: d.combo,
        label: d.label,
        group: d.group,
        order: d.order,
        disabled: d.disabled,
        hidden: d.hidden,
        allowInInput: d.allowInInput,
        preventDefault: d.preventDefault,
        run: (e) => ref.current[i]?.run(e), // always the freshest handler
      }),
    );
    return () => { for (const u of unregs) u(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
}
